import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { meetingAPI, agendaAPI, userAPI } from '../api';
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { 
  ArrowLeft, Plus, GripVertical, Trash2, Clock, User,
  Loader2, Save, FileText, CheckCircle
} from 'lucide-react';
import { toast } from 'sonner';

// Sortable Agenda Item Component
const SortableAgendaItem = ({ item, index, users, onUpdate, onDelete }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const getUserName = (userId) => {
    const user = users.find(u => u.id === userId);
    return user?.full_name || '';
  };

  return (
    <div 
      ref={setNodeRef} 
      style={style}
      className="bg-white border border-[#E2E6EA] rounded-lg p-4 hover:border-[#00A3A3] transition-colors"
    >
      <div className="flex items-start gap-3">
        <button
          {...attributes}
          {...listeners}
          className="mt-1 cursor-grab active:cursor-grabbing p-1 hover:bg-[#F0F2F4] rounded"
        >
          <GripVertical className="h-5 w-5 text-[#A8B1B9]" />
        </button>
        
        <span className="flex-shrink-0 w-8 h-8 rounded-full bg-[#00A3A3]/10 text-[#00A3A3] flex items-center justify-center font-semibold text-sm mt-1">
          {index + 1}
        </span>
        
        <div className="flex-1 space-y-3">
          <Input
            value={item.title}
            onChange={(e) => onUpdate(item.id, { title: e.target.value })}
            placeholder="Agenda item title"
            className="border-[#E2E6EA] font-medium"
          />
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <Label className="text-xs text-[#5A6570]">Type</Label>
              <Select
                value={item.item_type}
                onValueChange={(value) => onUpdate(item.id, { item_type: value })}
              >
                <SelectTrigger className="mt-1 h-9 text-sm border-[#E2E6EA]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="for_approval">For Approval</SelectItem>
                  <SelectItem value="for_noting">For Noting</SelectItem>
                  <SelectItem value="for_discussion">For Discussion</SelectItem>
                  <SelectItem value="election">Election</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label className="text-xs text-[#5A6570]">Presenter</Label>
              <Select
                value={item.presenter || ''}
                onValueChange={(value) => onUpdate(item.id, { presenter: value || null })}
              >
                <SelectTrigger className="mt-1 h-9 text-sm border-[#E2E6EA]">
                  <SelectValue placeholder="None" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">None</SelectItem>
                  {users.map((user) => (
                    <SelectItem key={user.id} value={user.id}>{user.full_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label className="text-xs text-[#5A6570]">Est. Time (min)</Label>
              <Input
                type="number"
                min="1"
                value={item.estimated_minutes}
                onChange={(e) => onUpdate(item.id, { estimated_minutes: parseInt(e.target.value) || 15 })}
                className="mt-1 h-9 text-sm border-[#E2E6EA]"
              />
            </div>
            
            <div className="flex items-end">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onDelete(item.id)}
                className="h-9 text-red-600 hover:text-red-700 hover:bg-red-50"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
          
          <Textarea
            value={item.description || ''}
            onChange={(e) => onUpdate(item.id, { description: e.target.value })}
            placeholder="Description or notes (optional)"
            className="border-[#E2E6EA] text-sm min-h-[60px]"
          />
        </div>
      </div>
    </div>
  );
};

const AgendaBuilder = () => {
  const { id: meetingId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [meeting, setMeeting] = useState(null);
  const [agendaItems, setAgendaItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    fetchData();
  }, [meetingId]);

  const fetchData = async () => {
    try {
      const [meetingRes, agendaRes, usersRes] = await Promise.all([
        meetingAPI.get(meetingId),
        agendaAPI.getByMeeting(meetingId),
        userAPI.getAll(),
      ]);

      setMeeting(meetingRes.data);
      setAgendaItems(agendaRes.data);
      setUsers(usersRes.data);
    } catch (err) {
      console.error('Failed to fetch data:', err);
      toast.error('Failed to load meeting data');
    } finally {
      setLoading(false);
    }
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      setAgendaItems((items) => {
        const oldIndex = items.findIndex((i) => i.id === active.id);
        const newIndex = items.findIndex((i) => i.id === over.id);
        
        const newItems = arrayMove(items, oldIndex, newIndex).map((item, idx) => ({
          ...item,
          order: idx
        }));
        
        setHasChanges(true);
        return newItems;
      });
    }
  };

  const addItem = () => {
    const newItem = {
      id: `temp-${Date.now()}`,
      meeting_id: meetingId,
      title: '',
      item_type: 'for_noting',
      description: '',
      presenter: null,
      estimated_minutes: 15,
      order: agendaItems.length,
      isNew: true
    };
    setAgendaItems([...agendaItems, newItem]);
    setHasChanges(true);
  };

  const updateItem = (itemId, updates) => {
    setAgendaItems(items => items.map(item => 
      item.id === itemId ? { ...item, ...updates } : item
    ));
    setHasChanges(true);
  };

  const deleteItem = async (itemId) => {
    const item = agendaItems.find(i => i.id === itemId);
    
    if (item.isNew) {
      setAgendaItems(items => items.filter(i => i.id !== itemId));
    } else {
      try {
        await agendaAPI.delete(itemId);
        setAgendaItems(items => items.filter(i => i.id !== itemId));
        toast.success('Item deleted');
      } catch (err) {
        toast.error('Failed to delete item');
      }
    }
    setHasChanges(true);
  };

  const saveAgenda = async () => {
    setSaving(true);
    try {
      // Save new items
      for (const item of agendaItems) {
        if (item.isNew) {
          const { id, isNew, ...itemData } = item;
          await agendaAPI.create({ ...itemData, meeting_id: meetingId });
        } else {
          await agendaAPI.update(item.id, {
            title: item.title,
            item_type: item.item_type,
            description: item.description,
            presenter: item.presenter,
            estimated_minutes: item.estimated_minutes,
            order: item.order
          });
        }
      }
      
      // Reorder items
      await agendaAPI.reorder(agendaItems.map((item, idx) => ({ id: item.id, order: idx })));
      
      toast.success('Agenda saved successfully');
      setHasChanges(false);
      fetchData(); // Refresh to get proper IDs
    } catch (err) {
      toast.error('Failed to save agenda');
    } finally {
      setSaving(false);
    }
  };

  const getTotalTime = () => {
    return agendaItems.reduce((sum, item) => sum + (item.estimated_minutes || 0), 0);
  };

  // Add default PH agenda items
  const addDefaultItems = () => {
    const meetingType = meeting?.meeting_type || 'board';
    const defaultItems = getDefaultAgendaItems(meetingType);
    
    const newItems = defaultItems.map((item, idx) => ({
      id: `temp-${Date.now()}-${idx}`,
      meeting_id: meetingId,
      ...item,
      order: agendaItems.length + idx,
      isNew: true
    }));
    
    setAgendaItems([...agendaItems, ...newItems]);
    setHasChanges(true);
    toast.success('Default items added');
  };

  const getDefaultAgendaItems = (type) => {
    if (type === 'board') {
      return [
        { title: 'Call to Order', item_type: 'other', estimated_minutes: 5 },
        { title: 'Proof of Notice and Determination of Quorum', item_type: 'for_noting', estimated_minutes: 5 },
        { title: 'Approval of Minutes of Previous Meeting', item_type: 'for_approval', estimated_minutes: 10 },
        { title: 'Matters Arising from Previous Minutes', item_type: 'for_discussion', estimated_minutes: 15 },
        { title: 'Reports', item_type: 'for_noting', estimated_minutes: 20 },
        { title: 'New Business', item_type: 'for_discussion', estimated_minutes: 30 },
        { title: 'Other Matters', item_type: 'for_discussion', estimated_minutes: 10 },
        { title: 'Adjournment', item_type: 'other', estimated_minutes: 5 },
      ];
    } else if (type === 'asm' || type === 'ssm') {
      return [
        { title: 'Call to Order', item_type: 'other', estimated_minutes: 5 },
        { title: 'Proof of Notice and Determination of Quorum', item_type: 'for_noting', estimated_minutes: 10 },
        { title: 'Approval of Minutes of Previous Meeting', item_type: 'for_approval', estimated_minutes: 10 },
        { title: "President's Report", item_type: 'for_noting', estimated_minutes: 20 },
        { title: "Treasurer's Report and Approval of Financial Statements", item_type: 'for_approval', estimated_minutes: 20 },
        { title: 'Ratification of Acts of the Board of Directors', item_type: 'for_approval', estimated_minutes: 15 },
        { title: 'Election of Directors', item_type: 'election', estimated_minutes: 30 },
        { title: 'Appointment of External Auditor', item_type: 'for_approval', estimated_minutes: 10 },
        { title: 'Other Matters', item_type: 'for_discussion', estimated_minutes: 10 },
        { title: 'Adjournment', item_type: 'other', estimated_minutes: 5 },
      ];
    }
    return [
      { title: 'Call to Order', item_type: 'other', estimated_minutes: 5 },
      { title: 'Proof of Notice and Determination of Quorum', item_type: 'for_noting', estimated_minutes: 5 },
      { title: 'Matters for Discussion', item_type: 'for_discussion', estimated_minutes: 30 },
      { title: 'Adjournment', item_type: 'other', estimated_minutes: 5 },
    ];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-[#00A3A3]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="agenda-builder">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-start gap-4">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate(`/meetings/${meetingId}`)}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-[#1A1D21]">Agenda Builder</h1>
            <p className="text-[#5A6570] mt-1">{meeting?.title}</p>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 text-sm text-[#5A6570] bg-[#F0F2F4] px-3 py-2 rounded-lg">
            <Clock className="h-4 w-4" />
            <span>Est. Duration: <strong>{getTotalTime()} min</strong></span>
          </div>
          
          <Button
            variant="outline"
            onClick={addDefaultItems}
            className="border-[#E2E6EA]"
          >
            <FileText className="h-4 w-4 mr-2" />
            Add Default Items
          </Button>
          
          <Button
            onClick={saveAgenda}
            disabled={saving || !hasChanges}
            className="bg-[#00A3A3] hover:bg-[#008585]"
          >
            {saving ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            Save Agenda
          </Button>
        </div>
      </div>

      {/* Agenda Items */}
      <Card className="border-[#E2E6EA]">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">Agenda Items</CardTitle>
            <span className="text-sm text-[#5A6570]">{agendaItems.length} items</span>
          </div>
        </CardHeader>
        <CardContent>
          {agendaItems.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="h-12 w-12 mx-auto mb-4 text-[#A8B1B9]" />
              <h3 className="text-lg font-medium text-[#1A1D21] mb-2">No agenda items yet</h3>
              <p className="text-[#5A6570] mb-4">
                Start by adding items or use the default template
              </p>
              <div className="flex justify-center gap-3">
                <Button onClick={addItem} className="bg-[#00A3A3] hover:bg-[#008585]">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Item
                </Button>
                <Button variant="outline" onClick={addDefaultItems} className="border-[#E2E6EA]">
                  <FileText className="h-4 w-4 mr-2" />
                  Use Default Template
                </Button>
              </div>
            </div>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={agendaItems.map(i => i.id)}
                strategy={verticalListSortingStrategy}
              >
                <div className="space-y-3">
                  {agendaItems.map((item, index) => (
                    <SortableAgendaItem
                      key={item.id}
                      item={item}
                      index={index}
                      users={users}
                      onUpdate={updateItem}
                      onDelete={deleteItem}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          )}
        </CardContent>
        {agendaItems.length > 0 && (
          <CardFooter className="border-t border-[#E2E6EA] bg-[#F8F9FA]">
            <Button onClick={addItem} variant="outline" className="border-[#E2E6EA]">
              <Plus className="h-4 w-4 mr-2" />
              Add Item
            </Button>
          </CardFooter>
        )}
      </Card>

      {/* Unsaved Changes Warning */}
      {hasChanges && (
        <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 bg-[#1A1D21] text-white px-6 py-3 rounded-lg shadow-lg flex items-center gap-4">
          <span>You have unsaved changes</span>
          <Button size="sm" onClick={saveAgenda} disabled={saving} className="bg-[#00A3A3] hover:bg-[#008585]">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save Now'}
          </Button>
        </div>
      )}
    </div>
  );
};

export default AgendaBuilder;
